/**
 * V2 Permission System - B2C Merchant Portal
 *
 * Single source of truth for all V2 permission keys, screen-to-permission
 * mappings, and core permission-checking logic for merchant users.
 *
 * V2 tokens are lean (no embedded authorities). Permissions are resolved
 * server-side via GET /v2/iam/permissions/resolve and cached client-side.
 *
 * @see PermissionResolutionEngine (IAM backend)
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';

/* ================================================================
 * 1) V2 Permission Key Constants
 * ================================================================
 * These keys mirror the V2 seed catalog (V2_STEP5_SEED_V2_CATALOG.sql).
 * Only merchant-facing keys (scope :own / :merchant) are included.
 * ================================================================ */

export const PERM = {
  // ─── OMS: Orders ───────────────────────────────────────────────
  OMS_ORDER_CREATE:             'oms.order:create',
  OMS_ORDER_READ_OWN:           'oms.order:read:own',
  OMS_ORDER_UPDATE_OWN:         'oms.order:update:own',
  OMS_ORDER_CANCEL_OWN:         'oms.order:cancel:own',
  OMS_ORDER_SEARCH_OWN:         'oms.order:search:own',
  OMS_ORDER_BULK_CREATE:        'oms.order:bulk-create',
  OMS_DRAFT_CREATE:             'oms.draft:create',
  OMS_DRAFT_UPDATE:             'oms.draft:update',
  OMS_DRAFT_CONVERT:            'oms.draft:convert',
  OMS_ORDER_LABEL_GENERATE:     'oms.order:label:generate',
  OMS_ORDER_LABEL_BULK_GENERATE:'oms.order:label:bulk-generate',
  OMS_ORDER_INVOICE_GENERATE:   'oms.order:invoice:generate',
  OMS_ORDER_TRACKING_READ:      'oms.order:tracking:read',
  OMS_ORDER_BULK_TRACKING_READ: 'oms.order:bulk-tracking:read',
  OMS_SERVICEABILITY_CHECK:     'oms.serviceability:check:merchant',
  OMS_CARRIER_VIEW_ALLOCATED:   'oms.carrier:view:allocated',

  // ─── OMS: NDR ──────────────────────────────────────────────────
  OMS_NDR_VIEW:                 'oms.ndr:view',
  OMS_NDR_ACTION_UPDATE:        'oms.ndr:action-update',

  // ─── POCAS: Claims ─────────────────────────────────────────────
  POCAS_CLAIM_CREATE:           'pocas.claim:create',
  POCAS_CLAIM_READ_OWN:         'pocas.claim:read:own',
  POCAS_CLAIM_UPDATE:           'pocas.claim:update',

  // ─── POCAS: Onboarding ─────────────────────────────────────────
  POCAS_ONBOARDING_READ_OWN:    'pocas.onboarding:read:own',
  POCAS_ONBOARDING_SUBMIT:      'pocas.onboarding:submit',

  // ─── POCAS: Reports & Finance ──────────────────────────────────
  POCAS_REPORT_READ_OWN:        'pocas.report:read:own',
  POCAS_REPORT_DOWNLOAD:        'pocas.report:download',
  POCAS_COD_REMITTANCE_READ_OWN:'pocas.cod-remittance:read:own',

  // ─── POCAS: Labels & Manifests ─────────────────────────────────
  POCAS_LABEL_GENERATE_OWN:     'pocas.label:generate:own',
  POCAS_MANIFEST_READ_OWN:      'pocas.manifest:read:own',
  POCAS_MANIFEST_CREATE:        'pocas.manifest:create',

  // ─── POCAS: Uploads ────────────────────────────────────────────
  POCAS_UPLOAD_MANAGE_OWN:      'pocas.upload:manage:own',

  // ─── POCAS: Report Config ──────────────────────────────────────
  POCAS_REPORT_CONFIG_MANAGE_OWN:'pocas.report-config:manage:own',
  POCAS_MIS_CONFIG_MANAGE_OWN:  'pocas.mis-config:manage:own',

  // ─── POCAS: Tickets ────────────────────────────────────────────
  POCAS_TICKET_CREATE:          'pocas.ticket:create',
  POCAS_TICKET_READ_OWN:        'pocas.ticket:read:own',
  POCAS_TICKET_UPDATE_OWN:      'pocas.ticket:update:own',
  POCAS_TICKET_MESSAGE_CREATE:  'pocas.ticket:message:create',

  // ─── Wayubridge: Marketplace ───────────────────────────────────
  WAYUBRIDGE_ORDERS_SYNC:       'wayubridge.orders:sync',
  WAYUBRIDGE_ORDERS_READ:       'wayubridge.orders:read',
  WAYUBRIDGE_ORDERS_UPDATE:     'wayubridge.orders:update',
  WAYUBRIDGE_STORE_CONFIG_MANAGE:'wayubridge.store-config:manage',
  WAYUBRIDGE_STORE_CONFIG_READ: 'wayubridge.store-config:read',

  // ─── IAM: Self-service ─────────────────────────────────────────
  IAM_USER_READ_OWN:            'iam.user:read:own',
  IAM_USER_UPDATE_OWN:          'iam.user:update:own',
  IAM_PASSWORD_CHANGE_OWN:      'iam.password:change:own',

  // ─── Finance: Wallet ───────────────────────────────────────────
  FINANCE_WALLET_READ_OWN:      'finance.wallet:read:own',
};

/* ================================================================
 * 2) Screen → Permission Mapping
 * ================================================================ */

export const SCREEN = {
  // Orders Management
  ALL_SHIPMENTS:         'ALL_SHIPMENTS',
  DRAFT_ORDERS:          'DRAFT_ORDERS',
  CREATE_FORWARD:        'CREATE_FORWARD',
  CREATE_BULK:           'CREATE_BULK',
  CREATE_REVERSE:        'CREATE_REVERSE',
  PICKUP_MANIFEST:       'PICKUP_MANIFEST',
  NDR_MANAGEMENT:        'NDR_MANAGEMENT',
  CLAIM_TRACKER:         'CLAIM_TRACKER',
  // Marketplace
  SHOPIFY_ORDERS:        'SHOPIFY_ORDERS',
  MARKETPLACE_SELF_SHIP: 'MARKETPLACE_SELF_SHIP',
  // Operations
  BULK_TRACKING:         'BULK_TRACKING',
  BULK_LABELS_DOWNLOAD:  'BULK_LABELS_DOWNLOAD',
  BULK_INVOICE_DOWNLOAD: 'BULK_INVOICE_DOWNLOAD',
  TOOLS:                 'TOOLS',
  UNPROCESSED_ORDERS:    'UNPROCESSED_ORDERS',
  SUPPORT_TICKETS:       'SUPPORT_TICKETS',
  // Reports & Finance
  REPORTS:               'REPORTS',
  COD_REMITTANCE:        'COD_REMITTANCE',
  WALLET:                'WALLET',
  // Control Center
  ACCOUNT_CONFIG:        'ACCOUNT_CONFIG',
  REPORT_CONFIG:         'REPORT_CONFIG',
  SCHEDULED_REPORTS:     'SCHEDULED_REPORTS',
  // Settings
  PROFILE:               'PROFILE',
};

export const SCREEN_REQUIREMENTS = {
  // Orders Management
  ALL_SHIPMENTS:         { anyOf: [PERM.OMS_ORDER_SEARCH_OWN, PERM.OMS_ORDER_READ_OWN] },
  DRAFT_ORDERS:          { anyOf: [PERM.OMS_DRAFT_CREATE, PERM.OMS_DRAFT_UPDATE] },
  CREATE_FORWARD:        { anyOf: [PERM.OMS_ORDER_CREATE] },
  CREATE_BULK:           { anyOf: [PERM.OMS_ORDER_BULK_CREATE] },
  CREATE_REVERSE:        { anyOf: [PERM.OMS_ORDER_CREATE] },
  PICKUP_MANIFEST:       { anyOf: [PERM.POCAS_MANIFEST_READ_OWN] },
  NDR_MANAGEMENT:        { anyOf: [PERM.OMS_NDR_VIEW] },
  CLAIM_TRACKER:         { anyOf: [PERM.POCAS_CLAIM_READ_OWN, PERM.POCAS_CLAIM_CREATE] },

  // Marketplace
  SHOPIFY_ORDERS:        { anyOf: [PERM.WAYUBRIDGE_ORDERS_SYNC] },
  MARKETPLACE_SELF_SHIP: { anyOf: [PERM.WAYUBRIDGE_ORDERS_READ] },

  // Operations
  BULK_TRACKING:         { anyOf: [PERM.OMS_ORDER_BULK_TRACKING_READ] },
  BULK_LABELS_DOWNLOAD:  { anyOf: [PERM.OMS_ORDER_LABEL_BULK_GENERATE] },
  BULK_INVOICE_DOWNLOAD: { anyOf: [PERM.OMS_ORDER_INVOICE_GENERATE] },
  TOOLS:                 { anyOf: [PERM.OMS_SERVICEABILITY_CHECK] },
  UNPROCESSED_ORDERS:    { anyOf: [PERM.OMS_ORDER_SEARCH_OWN] },
  SUPPORT_TICKETS:       { anyOf: [PERM.POCAS_TICKET_CREATE, PERM.POCAS_TICKET_READ_OWN] },

  // Reports & Finance
  REPORTS:               { anyOf: [PERM.POCAS_REPORT_READ_OWN] },
  COD_REMITTANCE:        { anyOf: [PERM.POCAS_COD_REMITTANCE_READ_OWN] },
  WALLET:                { anyOf: [PERM.FINANCE_WALLET_READ_OWN] },

  // Control Center
  ACCOUNT_CONFIG:        { anyOf: [PERM.POCAS_ONBOARDING_READ_OWN] },
  REPORT_CONFIG:         { anyOf: [PERM.POCAS_REPORT_CONFIG_MANAGE_OWN] },
  SCHEDULED_REPORTS:     { anyOf: [PERM.POCAS_REPORT_CONFIG_MANAGE_OWN] },

  // Settings (always visible)
  PROFILE:               {},
};

/* ================================================================
 * 3) Core Permission Checkers
 * ================================================================ */

/** Returns true if the set contains ANY of the wanted keys. */
export function hasAny(perms, wanted) {
  const list = Array.isArray(wanted) ? wanted : [wanted];
  return list.some((w) => perms.has(w));
}

/** Returns true if the set contains ALL of the wanted keys. */
export function hasAll(perms, wanted) {
  const list = Array.isArray(wanted) ? wanted : [wanted];
  return list.every((w) => perms.has(w));
}

/** Evaluates a permission requirement (anyOf + allOf logic). */
export function can(perms, req) {
  if (!req || (!req.anyOf && !req.allOf)) return true;
  if (req.anyOf && !hasAny(perms, req.anyOf)) return false;
  if (req.allOf && !hasAll(perms, req.allOf)) return false;
  return true;
}

/** Checks if a user can access a given screen. */
export function canScreen(perms, screen) {
  return can(perms, SCREEN_REQUIREMENTS[screen]);
}

/* ================================================================
 * 4) Response Normaliser (snake_case → camelCase)
 * ================================================================ */

export function normaliseSnapshot(raw) {
  return {
    clientId: raw.client_id,
    organizationId: raw.organization_id,
    userType: raw.user_type,
    platformId: raw.platform_id,
    superAdmin: raw.super_admin,
    roleNames: raw.role_names || [],
    v1AuthorityNames: raw.v1_authority_names || [],
    v2PermissionKeys: raw.v2_permission_keys || [],
    screenUrls: raw.screen_urls || [],
    // Explicit per-user screen grants: [{ screenUrl, screenName, accessLevel }]
    screenGrants: (raw.screen_grants || []).map((g) => ({
      screenUrl: g.screen_url,
      screenName: g.screen_name,
      accessLevel: g.access_level,
    })),
  };
}

/* ================================================================
 * 2b) SCREEN → { url, name } metadata
 * ================================================================
 * Bridges the SCREEN enum keys used for UI gating to the concrete
 * screen_url / screen_name values stored in IAM (identity_management.screens).
 * Per-user screen grants arrive keyed by url/name, so this map lets
 * canScreen() resolve a grant for a given SCREEN key. Keep in sync with the
 * Sidebar route table.
 * ================================================================ */

export const SCREEN_META = {
  ALL_SHIPMENTS:         { url: '/all-shipments', name: 'All Shipments' },
  DRAFT_ORDERS:          { url: '/draft-orders', name: 'Draft Orders' },
  CREATE_FORWARD:        { url: '/create-forward-order', name: 'Create Forward Order' },
  CREATE_BULK:           { url: '/create-bulk-order', name: 'Create Bulk Order' },
  CREATE_REVERSE:        { url: '/create-reverse-order', name: 'Create Reverse Order' },
  PICKUP_MANIFEST:       { url: '/pickup-manifest', name: 'Pickup Manifest' },
  NDR_MANAGEMENT:        { url: '/ndr-management', name: 'NDR' },
  CLAIM_TRACKER:         { url: '/claim-tracker', name: 'Claim' },
  SHOPIFY_ORDERS:        { url: '/channel-order', name: 'Shopify Orders' },
  MARKETPLACE_SELF_SHIP: { url: '/marketplace-self-ship', name: 'Marketplace Self-Ship Orders' },
  BULK_TRACKING:         { url: '/bulk-tracking', name: 'Bulk Tracking' },
  BULK_LABELS_DOWNLOAD:  { url: '/shipping-bulk-labels-download', name: 'Bulk Labels Download' },
  BULK_INVOICE_DOWNLOAD: { url: '/bulk-invoice-download', name: 'Bulk Invoice Download' },
  TOOLS:                 { url: '/tools', name: 'Tools' },
  UNPROCESSED_ORDERS:    { url: '/search-unprocessed-orders', name: 'Unprocessed Orders' },
  SUPPORT_TICKETS:       { url: '/support/tickets', name: 'Support Tickets' },
  REPORTS:               { url: '/reports', name: 'Reports' },
  COD_REMITTANCE:        { url: '/cod-remittance-reports', name: 'COD Remittance Report' },
  WALLET:                { url: '/wallet', name: 'Wallet' },
  ACCOUNT_CONFIG:        { url: '/settings/configuration', name: 'Account Configuration' },
  REPORT_CONFIG:         { url: '/settings/report-configuration', name: 'Report Configuration' },
  SCHEDULED_REPORTS:     { url: '/settings/scheduled-email-reports', name: 'Scheduled Email Reports' },
  PROFILE:               { url: '/profile', name: 'Profile' },
};

/* ================================================================
 * 5) Token Version Detection
 * ================================================================ */

function decodePayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

export function isV2Token(token) {
  if (!token) return false;
  const decoded = decodePayload(token);
  return decoded?.tver === 2;
}

/* ================================================================
 * 6) React Hook - usePermissions()
 * ================================================================ */

/**
 * User types that receive unrestricted access to every screen/permission in
 * the merchant portal:
 *   • ORG_ADMIN   - the merchant (organization) admin; the top role within a
 *                   merchant org, and must never see a locked screen.
 *   • SUPER_ADMIN - the platform operator.
 */
const FULL_ACCESS_USER_TYPES = new Set(['SUPER_ADMIN', 'ORG_ADMIN']);

export function usePermissions() {
  const snapshot = useSelector((s) => s.permissions?.snapshot);
  const status = useSelector((s) => s.permissions?.status);
  const token = useSelector((s) => s.auth?.token);

  return useMemo(() => {
    // Gating draws from BOTH permission sources on the snapshot:
    //  • v2PermissionKeys - resource:action:scope keys (V2 catalog)
    //  • v1AuthorityNames - legacy authority names. The oms.* / pocas.* keys
    //    granted to merchant users currently surface here, and these are what
    //    SCREEN_REQUIREMENTS reference. Merging keeps UI gating in parity with
    //    the authority set the downstream services (OMS, POCAS…) authorize
    //    against - otherwise a screen can lock while its data still loads.
    const keys = [
      ...(snapshot?.v2PermissionKeys || []),
      ...(snapshot?.v1AuthorityNames || []),
    ];
    const permSet = new Set(keys);

    const isSuperAdmin = snapshot?.superAdmin || false;
    // Merchant admin (ORG_ADMIN) and platform super admin get unrestricted
    // access - no screen is ever locked for them.
    const isFullAccess =
      isSuperAdmin || FULL_ACCESS_USER_TYPES.has(snapshot?.userType);

    // Explicit per-user screen grants. When the user has ANY, they are the
    // authoritative source for screen visibility (and write access); otherwise
    // we fall back to deriving visibility from permission keys (legacy).
    const screenGrants = snapshot?.screenGrants || [];
    const hasExplicitScreens = screenGrants.length > 0;
    const grantedScreens = new Set();
    const writableScreens = new Set();
    screenGrants.forEach((g) => {
      [g.screenUrl, g.screenName].forEach((k) => {
        if (!k) return;
        grantedScreens.add(k);
        if (g.accessLevel === 'READ_WRITE') writableScreens.add(k);
      });
    });
    const matchGrant = (set, screen) => {
      const meta = SCREEN_META[screen];
      return !!meta && (set.has(meta.url) || set.has(meta.name));
    };

    const effectiveHasAny = (wanted) =>
      isFullAccess || hasAny(permSet, wanted);
    const effectiveHasAll = (wanted) =>
      isFullAccess || hasAll(permSet, wanted);
    const effectiveCan = (req) =>
      isFullAccess || can(permSet, req);
    const effectiveCanScreen = (screen) => {
      if (isFullAccess) return true;
      if (hasExplicitScreens) return matchGrant(grantedScreens, screen);
      return canScreen(permSet, screen);
    };
    const effectiveCanWriteScreen = (screen) => {
      if (isFullAccess) return true;
      if (hasExplicitScreens) return matchGrant(writableScreens, screen);
      return canScreen(permSet, screen); // no RW data → visible implies writable (legacy)
    };

    return {
      snapshot,
      status: status || 'idle',
      isReady: status === 'resolved',
      isSuperAdmin,
      isFullAccess,
      token,
      permissionKeys: permSet,
      roleNames: snapshot?.roleNames || [],
      hasAny: effectiveHasAny,
      hasAll: effectiveHasAll,
      can: effectiveCan,
      canScreen: effectiveCanScreen,
      canWriteScreen: effectiveCanWriteScreen,
    };
  }, [snapshot, status, token]);
}
